import {
    GRID_COLS,
    GRID_ROWS,
    getUsableGridRows,
    MODULES,
    getGridState,
    getPlacedModulesMap,
    getUnloadedCoreModules,
    getUnloadedOtherModules,
    placeModule,
    removeModule,
    canPlaceModule,
    isLoaded,
} from "../systems/memory.js";
import { isDebugModeVisible } from "../systems/debugState.js";
import { queueMemoryTutorialNarration } from "../systems/roomNarration.js";
import { playModuleInstall } from "../systems/audio.js";
import { getRoomAt } from "../systems/rooms.js";

/**
 * RAM Memory Manager UI System
 */

export const UI_CONFIG = {
    cell: { size: 26, gap: 2, radius: 3 },
    outerMargin: 16,
    headerHeight: 36,
    listWidth: 145,
    listGap: 16,
    storage: {
        itemHeight: 38,
        itemGap: 6,
        previewCell: 5,
        previewGap: 1,
        previewBox: 30,
        scrollStep: 44,
        scrollbarWidth: 4,
    },
    typography: {
        headerTitleSize: 13,
        moduleNameSize: 9,
        hintSize: 10,
    },
    drag: {
        dragOpacity: 0.85,
        validColor: [0, 220, 100],
        invalidColor: [220, 50, 50],
        removeColor: [200, 40, 50],
    },
};

const MEMORY_UI_Z = 10000;
const TALL_PREVIEW_MODULES = new Set(["story_owner"]);

let isOpen = false;
let draggedModuleId = null;
let dragOffset = { x: 0, y: 0 };
let currentMousePos = { x: 0, y: 0 };
let activePanelBounds = { x: 0, y: 0, w: 0, h: 0 };
let eventsInitialized = false;
let eventHandles = [];
let storageScrollY = 0;
let tutorialLockActive = false;
let closeListeners = new Set();
let memoryTutorialPromptQueued = false;

// Hover tracking for DISK inventory description tip
let hoveredDiskModuleId = null;
let hoverTimer = 0;
const HOVER_TIP_TAG = "hover_module_tip";
const HOVER_TIP_DELAY = 0.12;
let hoverTipBounds = null;
let hoverTipModId = null;
let hoverTipNodes = null; // { bg, title, body }

// Ghost drag cells reused across mouse moves
let ghostCellNodes = [];

function clearHoverTip() {
    hoverTipBounds = null;
    hoverTipModId = null;
    hoverTipNodes = null;
    destroyAll(HOVER_TIP_TAG);
}

function clearGhostCells() {
    ghostCellNodes = [];
    destroyAll("ghost_ui");
}

function trackEventHandle(handle) {
    if (handle && typeof handle.cancel === "function") {
        eventHandles.push(handle);
    }
    return handle;
}

function initMemoryUIEvents() {
    if (eventsInitialized) return;
    eventsInitialized = true;

    trackEventHandle(onResize(() => {
        if (isOpen) {
            renderMemoryUI();
        }
    }));

    trackEventHandle(onUpdate(() => {
        if (!isOpen) return;

        const m = mousePos();

        if (draggedModuleId && (m.x !== currentMousePos.x || m.y !== currentMousePos.y)) {
            currentMousePos = { x: m.x, y: m.y };
            renderGhostModule(getLayoutGeometry());
        }

        // DISK inventory hover → text description tip
        if (!draggedModuleId) {
            const diskItems = get("memory_ui_disk");
            let foundHovered = null;

            for (const item of diskItems) {
                const itemX = item.pos.x;
                const itemY = item.pos.y;
                if (m.x >= itemX && m.x <= itemX + UI_CONFIG.listWidth && m.y >= itemY && m.y <= itemY + UI_CONFIG.storage.itemHeight) {
                    foundHovered = item.moduleId;
                    break;
                }
            }

            if (foundHovered) {
                if (hoveredDiskModuleId === foundHovered) {
                    hoverTimer += dt();
                    if (hoverTimer >= HOVER_TIP_DELAY) {
                        renderHoverModuleTip(foundHovered, m);
                    }
                } else {
                    hoveredDiskModuleId = foundHovered;
                    hoverTimer = 0;
                    clearHoverTip();
                }
            } else if (hoveredDiskModuleId !== null) {
                hoveredDiskModuleId = null;
                hoverTimer = 0;
                clearHoverTip();
            }
        } else {
            clearHoverTip();
        }
    }));

    trackEventHandle(onMouseRelease(() => {
        if (!isOpen || !draggedModuleId) return;

        const m = mousePos();
        const layout = getLayoutGeometry();
        const { cell } = UI_CONFIG;

        const ghostTopLeftX = m.x - dragOffset.x;
        const ghostTopLeftY = m.y - dragOffset.y;

        const targetCol = Math.round((ghostTopLeftX - layout.gridX) / (cell.size + cell.gap));
        const targetRow = Math.round((ghostTopLeftY - layout.gridY) / (cell.size + cell.gap));

        const isOverGrid = targetRow >= 0 && targetRow < getUsableGridRows() && targetCol >= 0 && targetCol < GRID_COLS;

        if (isOverGrid) {
            if (placeModule(draggedModuleId, targetRow, targetCol)) {
                playModuleInstall();
            }
        } else {
            removeModule(draggedModuleId);
        }

        draggedModuleId = null;
        clearGhostCells();
        clearHoverTip();
        renderMemoryUI();
    }));

    trackEventHandle(onScroll((delta) => {
        if (!isOpen || draggedModuleId) return;

        const m = mousePos();
        const layout = getLayoutGeometry();
        const overStorage = (
            m.x >= layout.listX
            && m.x <= layout.listX + layout.listWidth
            && m.y >= layout.listY
            && m.y <= layout.listY + layout.gridH
        );

        if (!overStorage) return;

        const scrollDir = delta.y > 0 ? 1 : -1;
        storageScrollY += scrollDir * UI_CONFIG.storage.scrollStep;
        clampStorageScroll(layout);
        hoveredDiskModuleId = null;
        hoverTimer = 0;
        clearHoverTip();
        renderMemoryUI();
    }));
}

export function isMemoryUIOpen() {
    return isOpen;
}

export function isMemoryTutorialLocked() {
    return tutorialLockActive;
}

function canEndMemoryTutorial() {
    return isLoaded("core_locomotion") && isLoaded("core_vision");
}

export function setMemoryTutorialLock(active) {
    tutorialLockActive = !!active;
}

export function isMouseOverMemoryPanel(m) {
    if (!isOpen) return false;
    const { x, y, w, h } = activePanelBounds;
    return m.x >= x && m.x <= x + w && m.y >= y && m.y <= y + h;
}

export function getLayoutGeometry() {
    const screenW = width();
    const { cell, outerMargin, headerHeight, listWidth, listGap } = UI_CONFIG;

    const gridW = GRID_COLS * cell.size + (GRID_COLS - 1) * cell.gap;
    const gridH = GRID_ROWS * cell.size + (GRID_ROWS - 1) * cell.gap;

    const panelW = outerMargin * 2 + gridW + listGap + listWidth;
    const panelH = outerMargin * 2 + headerHeight + gridH;

    const panelX = Math.round((screenW - panelW) / 2);
    const panelY = 24;

    const gridX = panelX + outerMargin;
    const gridY = panelY + outerMargin + headerHeight;

    const listX = gridX + gridW + listGap;
    const listY = gridY;

    activePanelBounds = { x: panelX, y: panelY, w: panelW, h: panelH };

    return {
        panelX, panelY, panelW, panelH,
        gridX, gridY, gridW, gridH,
        listX, listY, listWidth,
    };
}

/** Screen-space bounds of the storage hover tip, for lighting cutouts. */
export function getMemoryHoverTipBounds() {
    return hoverTipBounds;
}

const MEMORY_TUTORIAL_PROMPT_BEAT = 0.65;

function maybeScheduleMemoryTutorialPrompt() {
    if (!tutorialLockActive || memoryTutorialPromptQueued) return;
    memoryTutorialPromptQueued = true;
    wait(MEMORY_TUTORIAL_PROMPT_BEAT, () => {
        if (isOpen && tutorialLockActive) {
            queueMemoryTutorialNarration();
        }
    });
}

export function toggleMemoryUI() {
    if (isOpen && tutorialLockActive && !canEndMemoryTutorial()) return;
    if (isOpen && tutorialLockActive && canEndMemoryTutorial()) {
        tutorialLockActive = false;
    }

    isOpen = !isOpen;
    if (isOpen) {
        draggedModuleId = null;
        renderMemoryUI();
        maybeScheduleMemoryTutorialPrompt();
    } else {
        closeUI();
    }
}

export function openMemoryUI() {
    if (isOpen) {
        renderMemoryUI();
        return;
    }

    isOpen = true;
    draggedModuleId = null;
    renderMemoryUI();
    maybeScheduleMemoryTutorialPrompt();
}

export function onMemoryUIClose(callback) {
    closeListeners.add(callback);
    return {
        cancel: () => closeListeners.delete(callback),
    };
}

export function closeUI(options = {}) {
    if (tutorialLockActive && !canEndMemoryTutorial()) {
        isOpen = true;
        renderMemoryUI();
        return;
    }
    if (tutorialLockActive) {
        tutorialLockActive = false;
    }

    const wasOpen = isOpen;
    isOpen = false;
    draggedModuleId = null;
    if (typeof destroyAll === "function") {
        destroyAll("memory_ui");
        clearGhostCells();
        clearHoverTip();
    }

    if (wasOpen && options.emit !== false) {
        closeListeners.forEach((callback) => callback());
    }
}

export function resetMemoryUIForScene() {
    tutorialLockActive = false;
    memoryTutorialPromptQueued = false;
    closeUI({ emit: false });
    closeListeners.clear();
    activePanelBounds = { x: 0, y: 0, w: 0, h: 0 };
    currentMousePos = { x: 0, y: 0 };
    storageScrollY = 0;

    eventHandles.forEach((handle) => handle.cancel());
    eventHandles = [];
    eventsInitialized = false;
}

function renderMiniModuleShape(mod, x, y, miniSize, miniGap, zBase, tag = "memory_ui") {
    mod.shape.forEach(([rOff, cOff]) => {
        add([
            rect(miniSize, miniSize, { radius: 1 }),
            pos(x + cOff * (miniSize + miniGap), y + rOff * (miniSize + miniGap)),
            color(mod.color[0], mod.color[1], mod.color[2]),
            outline(1, rgb(255, 255, 255)),
            fixed(),
            z(zBase),
            tag,
        ]);
    });
}

function getStorageItemHeight(mod) {
    if (TALL_PREVIEW_MODULES.has(mod.id)) return 92;
    return UI_CONFIG.storage.itemHeight;
}

function getStoragePreviewBox(mod) {
    if (TALL_PREVIEW_MODULES.has(mod.id)) return 76;
    return UI_CONFIG.storage.previewBox;
}

function getStorageContentHeight() {
    const unloadedCores = getUnloadedCoreModules();
    const unloadedOthers = getUnloadedOtherModules({
        includeLocked: isDebugModeVisible(),
        roomId: getPlayerRoomId(),
    });
    const { storage } = UI_CONFIG;
    let total = 0;

    if (unloadedCores.length > 0) {
        total += unloadedCores.reduce((sum, mod) => sum + getStorageItemHeight(mod), 0);
        total += Math.max(0, unloadedCores.length - 1) * storage.itemGap;
        total += 4;
    }

    if (unloadedCores.length > 0 && unloadedOthers.length > 0) {
        total += 8;
    }

    if (unloadedOthers.length > 0) {
        total += unloadedOthers.reduce((sum, mod) => sum + getStorageItemHeight(mod), 0);
        total += Math.max(0, unloadedOthers.length - 1) * storage.itemGap;
    }

    return total;
}

function getPlayerRoomId() {
    const player = get("player")?.[0];
    if (!player?.exists?.()) return null;
    return getRoomAt(player.pos)?.id ?? null;
}

function clampStorageScroll(layout) {
    const maxScroll = Math.max(0, getStorageContentHeight() - layout.gridH);
    storageScrollY = Math.max(0, Math.min(maxScroll, storageScrollY));
}

function isStorageRowVisible(y, height, layout) {
    const top = layout.listY;
    const bottom = layout.listY + layout.gridH;
    return y + height > top && y < bottom;
}

function renderStorageScrollbar(layout) {
    const contentH = getStorageContentHeight();
    if (contentH <= layout.gridH) return;

    const { storage } = UI_CONFIG;
    const trackX = layout.listX + layout.listWidth - storage.scrollbarWidth;
    const trackY = layout.listY;
    const trackH = layout.gridH;
    const thumbH = Math.max(18, Math.round((layout.gridH / contentH) * trackH));
    const maxScroll = Math.max(1, contentH - layout.gridH);
    const thumbY = trackY + Math.round((storageScrollY / maxScroll) * (trackH - thumbH));

    add([
        rect(storage.scrollbarWidth, trackH),
        pos(trackX, trackY),
        color(12, 15, 20),
        fixed(),
        z(MEMORY_UI_Z + 2),
        "memory_ui",
    ]);

    add([
        rect(storage.scrollbarWidth, thumbH),
        pos(trackX, thumbY),
        color(255, 200, 100),
        fixed(),
        z(MEMORY_UI_Z + 3),
        "memory_ui",
    ]);
}

export function renderMemoryUI() {
    initMemoryUIEvents();
    destroyAll("memory_ui");
    clearHoverTip();
    if (!isOpen) return;

    const layout = getLayoutGeometry();
    const { cell, outerMargin, headerHeight, typography, listWidth } = UI_CONFIG;
    const { panelX, panelY, panelW, panelH, gridX, gridY, listX, listY } = layout;
    clampStorageScroll(layout);

    // Panel Background Box
    add([
        rect(panelW, panelH, { radius: 4 }),
        pos(panelX, panelY),
        color(35, 40, 48),
        outline(2, rgb(70, 80, 95)),
        fixed(),
        z(MEMORY_UI_Z),
        "memory_ui",
    ]);

    add([
        text("MEMORY", { font: "ShareTechMono", size: typography.headerTitleSize }),
        pos(gridX, panelY + 10),
        color(0, 255, 140),
        fixed(),
        z(MEMORY_UI_Z + 1),
        "memory_ui",
    ]);

    add([
        text("STORAGE", { font: "ShareTechMono", size: typography.headerTitleSize }),
        pos(listX, panelY + 10),
        color(255, 200, 100),
        fixed(),
        z(MEMORY_UI_Z + 1),
        "memory_ui",
    ]);

    if (!tutorialLockActive || canEndMemoryTutorial()) {
        // Close Button Box
        const closeBtn = add([
            rect(18, 18, { radius: 2 }),
            pos(panelX + panelW - outerMargin - 18, panelY + 8),
            color(55, 60, 70),
            area({ isSensor: true }),
            fixed(),
            z(MEMORY_UI_Z + 1),
            "memory_ui",
            "memory_ui_close",
        ]);

        add([
            text("X", { font: "ShareTechMono", size: 10 }),
            pos(panelX + panelW - outerMargin - 9, panelY + 17),
            anchor("center"),
            color(200, 200, 200),
            fixed(),
            z(MEMORY_UI_Z + 3),
            "memory_ui",
        ]);

        closeBtn.onClick(() => closeUI());
    }

    // Separator Line
    add([
        rect(panelW - outerMargin * 2, 1),
        pos(panelX + outerMargin, panelY + headerHeight - 4),
        color(70, 80, 95),
        fixed(),
        z(MEMORY_UI_Z + 1),
        "memory_ui",
    ]);

    // Grid Cells Background
    const activeRows = getUsableGridRows();
    for (let r = 0; r < GRID_ROWS; r++) {
        for (let c = 0; c < GRID_COLS; c++) {
            const cx = gridX + c * (cell.size + cell.gap);
            const cy = gridY + r * (cell.size + cell.gap);
            const isCorrupted = r >= activeRows;

            add([
                rect(cell.size, cell.size, { radius: cell.radius }),
                pos(cx, cy),
                color(isCorrupted ? 120 : 15, isCorrupted ? 24 : 18, isCorrupted ? 28 : 22),
                opacity(isCorrupted ? 0.42 : 1),
                outline(1, isCorrupted ? rgb(160, 44, 52) : rgb(40, 45, 55)),
                fixed(),
                z(MEMORY_UI_Z + 1),
                "memory_ui",
            ]);
        }
    }

    const gridState = getGridState();
    const placedMap = getPlacedModulesMap();

    // Render Installed Modules in RAM as solid blocks
    placedMap.forEach((posInfo, modId) => {
        if (modId === draggedModuleId) return;

        const mod = MODULES[modId];
        if (!mod) return;

        const mx = gridX + posInfo.col * (cell.size + cell.gap);
        const my = gridY + posInfo.row * (cell.size + cell.gap);

        const occupiedCols = Math.max(...mod.shape.map(p => p[1])) + 1;
        const occupiedRows = Math.max(...mod.shape.map(p => p[0])) + 1;

        const boundingW = occupiedCols * cell.size + (occupiedCols - 1) * cell.gap;
        const boundingH = occupiedRows * cell.size + (occupiedRows - 1) * cell.gap;

        const cellSet = new Set(mod.shape.map(([r, c]) => `${r},${c}`));

        const modHitbox = add([
            pos(mx, my),
            rect(boundingW, boundingH),
            opacity(0),
            area({ isSensor: true }),
            fixed(),
            z(MEMORY_UI_Z + 2),
            "memory_ui",
            "memory_ui_placed",
            { moduleId: modId },
        ]);

        mod.shape.forEach(([rOff, cOff]) => {
            const cx = mx + cOff * (cell.size + cell.gap);
            const cy = my + rOff * (cell.size + cell.gap);

            const hasRight = cellSet.has(`${rOff},${cOff + 1}`);
            const hasBottom = cellSet.has(`${rOff + 1},${cOff}`);

            const cellW = cell.size + (hasRight ? cell.gap : 0);
            const cellH = cell.size + (hasBottom ? cell.gap : 0);

            // Fill solid cell color
            add([
                rect(cellW, cellH),
                pos(cx, cy),
                color(mod.color[0], mod.color[1], mod.color[2]),
                fixed(),
                z(MEMORY_UI_Z + 2),
                "memory_ui",
            ]);

            // Outer white borders around perimeter edges
            const topEdge = !cellSet.has(`${rOff - 1},${cOff}`);
            const bottomEdge = !cellSet.has(`${rOff + 1},${cOff}`);
            const leftEdge = !cellSet.has(`${rOff},${cOff - 1}`);
            const rightEdge = !cellSet.has(`${rOff},${cOff + 1}`);

            const bColor = rgb(255, 255, 255);
            const bThick = 1;

            if (topEdge) {
                add([rect(cellW, bThick), pos(cx, cy), color(bColor), fixed(), z(MEMORY_UI_Z + 4), "memory_ui"]);
            }
            if (bottomEdge) {
                add([rect(cellW, bThick), pos(cx, cy + cell.size - bThick), color(bColor), fixed(), z(MEMORY_UI_Z + 4), "memory_ui"]);
            }
            if (leftEdge) {
                add([rect(bThick, cellH), pos(cx, cy), color(bColor), fixed(), z(MEMORY_UI_Z + 4), "memory_ui"]);
            }
            if (rightEdge) {
                add([rect(bThick, cellH), pos(cx + cell.size - bThick, cy), color(bColor), fixed(), z(MEMORY_UI_Z + 4), "memory_ui"]);
            }
        });

        // Module Title: TOP LEFT of the module block
        add([
            text(mod.name, { font: "ShareTechMono", size: typography.moduleNameSize }),
            pos(mx + 4, my + 3),
            anchor("topleft"),
            color(255, 255, 255),
            fixed(),
            z(MEMORY_UI_Z + 5),
            "memory_ui",
        ]);

        // Keybind Hints: CENTER of the module shape
        let keyHint = null;
        if (modId === "core_locomotion") keyHint = "WASD";
        else if (modId === "action_bite") keyHint = "LMB";
        else if (modId === "action_bark") keyHint = "RMB";

        if (keyHint) {
            add([
                text(keyHint, { font: "ShareTechMono", size: typography.hintSize }),
                pos(mx + boundingW / 2, my + boundingH / 2 + (modId === "core_locomotion" ? 4 : 2)),
                anchor("center"),
                color(255, 255, 255),
                fixed(),
                z(MEMORY_UI_Z + 5),
                "memory_ui",
            ]);
        }

        modHitbox.onClick(() => {
            if (!draggedModuleId) {
                draggedModuleId = modId;
                const m = mousePos();
                dragOffset = { x: m.x - mx, y: m.y - my };
                renderMemoryUI();
            }
        });
    });

    // Render Unloaded Storage Modules
    const unloadedCores = getUnloadedCoreModules();
    const unloadedOthers = getUnloadedOtherModules({
        includeLocked: isDebugModeVisible(),
        roomId: getPlayerRoomId(),
    });
    let currentY = listY - storageScrollY;

    function renderModuleButton(mod, addGapAfter = true) {
        const { storage } = UI_CONFIG;
        const btnH = getStorageItemHeight(mod);
        const previewBox = getStoragePreviewBox(mod);
        const itemY = currentY;

        currentY += btnH + (addGapAfter ? storage.itemGap : 0);
        if (!isStorageRowVisible(itemY, btnH, layout)) return;

        const btn = add([
            rect(listWidth, btnH, { radius: 2 }),
            pos(listX, itemY),
            color(22, 26, 32),
            outline(1, rgb(mod.color[0], mod.color[1], mod.color[2])),
            area({ isSensor: true }),
            fixed(),
            z(MEMORY_UI_Z + 2),
            "memory_ui",
            "memory_ui_disk",
            { moduleId: mod.id },
        ]);

        const previewX = listX + 8;
        const previewY = itemY + Math.round((btnH - previewBox) / 2);

        add([
            rect(previewBox, previewBox, { radius: 2 }),
            pos(previewX, previewY),
            color(mod.color[0] * 0.18, mod.color[1] * 0.18, mod.color[2] * 0.18),
            fixed(),
            z(MEMORY_UI_Z + 3),
            "memory_ui",
        ]);

        const occupiedCols = Math.max(...mod.shape.map(p => p[1])) + 1;
        const occupiedRows = Math.max(...mod.shape.map(p => p[0])) + 1;
        const previewCell = Math.min(
            storage.previewCell,
            Math.floor((previewBox - (occupiedCols - 1) * storage.previewGap) / occupiedCols),
            Math.floor((previewBox - (occupiedRows - 1) * storage.previewGap) / occupiedRows),
        );
        const shapeW = occupiedCols * previewCell + (occupiedCols - 1) * storage.previewGap;
        const shapeH = occupiedRows * previewCell + (occupiedRows - 1) * storage.previewGap;
        renderMiniModuleShape(
            mod,
            previewX + Math.round((previewBox - shapeW) / 2),
            previewY + Math.round((previewBox - shapeH) / 2),
            previewCell,
            storage.previewGap,
            MEMORY_UI_Z + 4,
        );

        add([
            text(mod.name, { font: "ShareTechMono", size: typography.moduleNameSize }),
            pos(listX + previewBox + 14, itemY + Math.max(10, Math.round(btnH / 2) - 10)),
            anchor("topleft"),
            color(255, 255, 255),
            fixed(),
            z(MEMORY_UI_Z + 3),
            "memory_ui",
        ]);

        add([
            text(`${mod.shape.length} GB`, { font: "ShareTechMono", size: typography.moduleNameSize + 1 }),
            pos(listX + previewBox + 14, itemY + Math.max(23, Math.round(btnH / 2) + 3)),
            anchor("topleft"),
            color(170, 190, 210),
            fixed(),
            z(MEMORY_UI_Z + 3),
            "memory_ui",
        ]);

        btn.onClick(() => {
            if (!draggedModuleId) {
                draggedModuleId = mod.id;
                dragOffset = { x: previewBox / 2, y: btnH / 2 };
                renderMemoryUI();
            }
        });
    }

    if (unloadedCores.length > 0) {
        unloadedCores.forEach((mod, index) => {
            renderModuleButton(mod, index < unloadedCores.length - 1);
        });
        currentY += 4;
    }

    if (unloadedCores.length > 0 && unloadedOthers.length > 0) {
        if (currentY >= listY && currentY <= listY + layout.gridH) {
            add([
                rect(listWidth, 1),
                pos(listX, currentY),
                color(70, 80, 95),
                fixed(),
                z(MEMORY_UI_Z + 1),
                "memory_ui",
            ]);
        }
        currentY += 8;
    }

    if (unloadedOthers.length > 0) {
        unloadedOthers.forEach((mod, index) => {
            renderModuleButton(mod, index < unloadedOthers.length - 1);
        });
    }

    renderStorageScrollbar(layout);

    if (draggedModuleId) {
        renderGhostModule(layout);
    }
}

/**
 * Text description tip when hovering a DISK list item.
 * Reuses nodes while the same module stays hovered; only moves them with the cursor.
 */
function renderHoverModuleTip(modId, m) {
    const mod = MODULES[modId];
    if (!mod) return;

    const tipW = 200;
    const pad = 8;
    const titleSize = 11;
    const bodySize = 10;
    const lineGap = 4;
    const body = mod.description || `${mod.shape.length} GB module.`;
    const tipH = pad * 2 + titleSize + lineGap + bodySize * 3 + 4;

    let px = m.x + 14;
    let py = m.y - tipH / 2;
    if (px + tipW > width() - 8) px = m.x - tipW - 14;
    if (py < 8) py = 8;
    if (py + tipH > height() - 8) py = height() - tipH - 8;

    hoverTipBounds = { x: px, y: py, w: tipW, h: tipH, radius: 3 };

    const tipAlive = hoverTipNodes
        && hoverTipNodes.bg?.exists?.()
        && hoverTipNodes.title?.exists?.()
        && hoverTipNodes.body?.exists?.();

    if (hoverTipModId === modId && tipAlive) {
        hoverTipNodes.bg.pos = vec2(px, py);
        hoverTipNodes.title.pos = vec2(px + pad, py + pad);
        hoverTipNodes.body.pos = vec2(px + pad, py + pad + titleSize + lineGap);
        return;
    }

    clearHoverTip();
    hoverTipModId = modId;
    hoverTipBounds = { x: px, y: py, w: tipW, h: tipH, radius: 3 };

    const bg = add([
        rect(tipW, tipH, { radius: 3 }),
        pos(px, py),
        color(12, 16, 22),
        outline(1, rgb(mod.color[0], mod.color[1], mod.color[2])),
        opacity(0.96),
        fixed(),
        z(MEMORY_UI_Z + 95),
        HOVER_TIP_TAG,
    ]);

    const title = add([
        text(mod.name.toUpperCase(), { font: "ShareTechMono", size: titleSize }),
        pos(px + pad, py + pad),
        color(mod.color[0], mod.color[1], mod.color[2]),
        fixed(),
        z(MEMORY_UI_Z + 96),
        HOVER_TIP_TAG,
    ]);

    const bodyNode = add([
        text(body, { font: "ShareTechMono", size: bodySize, width: tipW - pad * 2 }),
        pos(px + pad, py + pad + titleSize + lineGap),
        color(160, 185, 205),
        fixed(),
        z(MEMORY_UI_Z + 96),
        HOVER_TIP_TAG,
    ]);

    hoverTipNodes = { bg, title, body: bodyNode };
}

function ensureGhostCellNodes(count) {
    const { cell } = UI_CONFIG;
    while (ghostCellNodes.length < count) {
        ghostCellNodes.push(add([
            rect(cell.size, cell.size, { radius: cell.radius }),
            pos(0, 0),
            color(255, 255, 255),
            opacity(UI_CONFIG.drag.dragOpacity),
            outline(1, rgb(255, 255, 255)),
            fixed(),
            z(MEMORY_UI_Z + 90),
            "ghost_ui",
        ]));
    }
    while (ghostCellNodes.length > count) {
        const node = ghostCellNodes.pop();
        if (node?.exists?.()) destroy(node);
    }
}

function renderGhostModule(layout) {
    const mod = MODULES[draggedModuleId];
    if (!mod) {
        clearGhostCells();
        return;
    }

    const m = mousePos();
    const { cell } = UI_CONFIG;
    const { gridX, gridY } = layout;

    const ghostTopLeftX = m.x - dragOffset.x;
    const ghostTopLeftY = m.y - dragOffset.y;

    const hoverCol = Math.round((ghostTopLeftX - gridX) / (cell.size + cell.gap));
    const hoverRow = Math.round((ghostTopLeftY - gridY) / (cell.size + cell.gap));

    const isOverGrid = hoverRow >= 0 && hoverRow < getUsableGridRows() && hoverCol >= 0 && hoverCol < GRID_COLS;
    const validPlacement = isOverGrid && canPlaceModule(draggedModuleId, hoverRow, hoverCol);

    let ghostColor;
    if (isOverGrid) {
        ghostColor = validPlacement ? UI_CONFIG.drag.validColor : UI_CONFIG.drag.invalidColor;
    } else {
        ghostColor = UI_CONFIG.drag.removeColor;
    }

    ensureGhostCellNodes(mod.shape.length);

    mod.shape.forEach(([rOff, cOff], index) => {
        let gx, gy;
        if (isOverGrid && validPlacement) {
            gx = gridX + (hoverCol + cOff) * (cell.size + cell.gap);
            gy = gridY + (hoverRow + rOff) * (cell.size + cell.gap);
        } else {
            gx = ghostTopLeftX + cOff * (cell.size + cell.gap);
            gy = ghostTopLeftY + rOff * (cell.size + cell.gap);
        }

        const node = ghostCellNodes[index];
        if (!node?.exists?.()) return;
        node.pos = vec2(gx, gy);
        node.color = rgb(ghostColor[0], ghostColor[1], ghostColor[2]);
        node.opacity = UI_CONFIG.drag.dragOpacity;
    });
}
